import {
    Directive,
    ElementRef,
    Input,
    HostListener,
    Renderer2,
    TemplateRef,
    ViewContainerRef,
    EmbeddedViewRef,
} from '@angular/core';

@Directive({
    selector: '[customTooltip]',
    standalone: true,
})
export class CustomTooltipDirective {
    @Input('tooltipTemplate') tooltipTemplate!: TemplateRef<any>; // Accepts a TemplateRef
    @Input('tooltipImg') tooltipImg!: string;

    private tooltipContainer: HTMLElement | null = null;
    private embeddedView: EmbeddedViewRef<any> | null = null;
    private isMobile: boolean = false;
    private tooltipTimeout: any;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private viewContainerRef: ViewContainerRef
    ) {
        this.isMobile = this.checkIfMobile();
    }

    @HostListener('mouseenter') onMouseEnter() {
        if (!this.tooltipContainer) {
            this.show();
        }
    }

    @HostListener('mouseleave') onMouseLeave() {
        if (this.tooltipContainer) {
            this.hide();
        }
    }

    @HostListener('touchstart') onTouchStart() {
        if (this.isMobile) {
            this.show();
            // Auto-close after 3 seconds
            this.tooltipTimeout = setTimeout(() => this.hide(), 2000);
        }
    }

    private show() {
        if (!this.tooltipTemplate && !this.tooltipImg) return;
        // Create tooltip container
        this.tooltipContainer = this.renderer.createElement('div');
        this.renderer.appendChild(this.el.nativeElement, this.tooltipContainer);

        this.tooltipContainer!.className =
            'tooltip custom-tooltip w-[325px] flex flex-col gap-3 p-4 absolute bg-white text-gray-950 text-sm rounded-lg transition-opacity opacity-0 font-light shadow-[0_4px_18px_0_rgba(0,0,0,0.17)]';
        this.renderer.setStyle(this.tooltipContainer, 'opacity', '1');

        // Dynamically fetch the global font-family
        const globalFontFamily = window
            .getComputedStyle(document.body)
            .getPropertyValue('font-family');
        this.renderer.setStyle(this.tooltipContainer, 'font-family', globalFontFamily);

        // Append tooltip to the DOM temporarily to measure its dimensions
        document.body.appendChild(this.tooltipContainer!);

        // Calculate positions
        const hostRect = this.el.nativeElement.getBoundingClientRect();
        const tooltipRect = this.tooltipContainer!.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let tooltipTop = hostRect.bottom + 8; // Default below the host
        let tooltipLeft = hostRect.left;

        // Adjust if the tooltip overflows the bottom of the viewport
        if (tooltipTop + tooltipRect.height > viewportHeight) {
            tooltipTop = hostRect.top - tooltipRect.height - 8; // Position above the host
        }

        // Adjust if the tooltip overflows the right of the viewport
        if (tooltipLeft + tooltipRect.width > viewportWidth) {
            tooltipLeft = viewportWidth - tooltipRect.width - 8; // Align with the right edge
        }

        // Adjust if the tooltip overflows the left of the viewport
        if (tooltipLeft < 0) {
            tooltipLeft = 8; // Add padding from the left edge
        }

        // Apply calculated positions
        this.renderer.setStyle(this.tooltipContainer, 'top', `${tooltipTop + window.scrollY}px`);
        this.renderer.setStyle(this.tooltipContainer, 'left', `${tooltipLeft + window.scrollX}px`);

        // WE HAVE TO CHECK HERE IF A MODAL EXISTS OPENED IN THE DOM so that we use greater z-index than it in order to show the tooltip
        const modalElement = document.querySelector('.modal');
        // Check if the modal exists
        if (modalElement) {
            // Get the computed styles of the modal
            const computedStyle = window.getComputedStyle(modalElement);

            // Access the z-index property
            const zIndex = computedStyle.zIndex;
            this.renderer.setStyle(this.tooltipContainer, 'z-index', `${zIndex + 50}`);
        } else {
            this.renderer.setStyle(this.tooltipContainer, 'z-index', '1000');
        }

        // Add content to the tooltip
        if (this.tooltipTemplate) {
            this.embeddedView = this.viewContainerRef.createEmbeddedView(
                this.tooltipTemplate
            );
            this.embeddedView.rootNodes.forEach((node) =>
                this.renderer.appendChild(this.tooltipContainer, node)
            );
        }

        // Add tooltip image if it exists
        if (this.tooltipImg) {
            const imgElement = this.renderer.createElement('img');
            this.renderer.setAttribute(imgElement, 'src', this.tooltipImg);
            this.renderer.setStyle(imgElement, 'width', '100%');
            this.renderer.appendChild(this.tooltipContainer, imgElement);
        }
    }

    private hide() {
        this.renderer.setStyle(this.tooltipContainer, 'opacity', '0');
        setTimeout(() => {
            if (this.tooltipContainer) {
                this.renderer.removeChild(document.body, this.tooltipContainer);
                this.tooltipContainer = null;
            }
            if (this.embeddedView) {
                this.embeddedView.destroy();
                this.embeddedView = null;
            }
        }, 300); // Match the transition duration

        // Clear timeout if it's a mobile auto-close
        if (this.tooltipTimeout) {
            clearTimeout(this.tooltipTimeout);
            this.tooltipTimeout = null;
        }
    }

    private checkIfMobile(): boolean {
        return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
            navigator.userAgent
        );
    }
}
